import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import {
  Mail,
  Clock,
  RefreshCw,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../lib/supabaseConfig";

const EmailVerificationRequired = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [showResendButton, setShowResendButton] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [gridDimensions, setGridDimensions] = useState({ rows: 0, cols: 0 });
  const [isVerified, setIsVerified] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Refs for animations
  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);

  // Get email from location state or localStorage
  useEffect(() => {
    const userEmail =
      location.state?.email || localStorage.getItem("pendingVerificationEmail");
    const verified = location.state?.verified;

    if (userEmail) {
      setEmail(userEmail);
      localStorage.setItem("pendingVerificationEmail", userEmail);

      // If user came back from successful verification
      if (verified) {
        setIsVerified(true);
        setSuccess("🎉 Email verified successfully! You can now log in.");
        // Auto-redirect to login after a short delay
        setTimeout(() => {
          localStorage.removeItem("pendingVerificationEmail");
          navigate("/login", {
            state: {
              email: userEmail,
              message: "Email verified! Please log in to access your account.",
            },
          });
        }, 3000);
      }
    } else {
      // Redirect to login if no email provided
      navigate("/login");
    }
  }, [location.state, navigate]);

  // Set grid dimensions
  useEffect(() => {
    const updateGridDimensions = () => {
      setGridDimensions({
        rows: Math.ceil(window.innerHeight / 40),
        cols: Math.ceil(window.innerWidth / 40),
      });
    };

    updateGridDimensions();
    window.addEventListener("resize", updateGridDimensions);

    return () => window.removeEventListener("resize", updateGridDimensions);
  }, []);

  // Auto-send verification email on component mount (only if not already verified)
  useEffect(() => {
    if (email && !isVerified) {
      // Check if user is already verified before sending email
      const checkAndSendVerification = async () => {
        try {
          // Try to get current session first
          const {
            data: { session },
          } = await supabase.auth.getSession();

          if (session?.user && session.user.email === email) {
            // User is logged in with the same email, check their verification status
            const { data, error } = await supabase.rpc("is_email_verified", {
              user_id: session.user.id,
            });

            if (!error && data === true) {
              // User is already verified, don't send email
              console.log("✅ User already verified, skipping auto-send");
              setIsVerified(true);
              setSuccess(
                "🎉 Email already verified! Redirecting to your account..."
              );
              setTimeout(() => {
                localStorage.removeItem("pendingVerificationEmail");
                navigate("/");
              }, 2000);
              return;
            }
          }

          // User is not verified, send verification email
          console.log("📧 Auto-sending verification email...");
          sendVerificationEmail(true); // true = auto-send, don't show success message
        } catch (error) {
          console.error("Error checking verification status:", error);
          // If check fails, proceed with sending email as fallback
          sendVerificationEmail(true);
        }
      };

      checkAndSendVerification();
    }
  }, [email, isVerified, navigate]);

  // Periodic verification status checking
  useEffect(() => {
    if (!email || isVerified) return;

    const checkVerificationStatus = async () => {
      if (checkingVerification) return;

      setCheckingVerification(true);
      try {
        // Check if there's a current session
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user && session.user.email === email) {
          // User is logged in with the same email, check their verification status
          const { data, error } = await supabase.rpc("is_email_verified", {
            user_id: session.user.id,
          });

          if (!error && data === true) {
            setIsVerified(true);
            setSuccess("🎉 Email verified! Redirecting to your account...");
            setTimeout(() => {
              localStorage.removeItem("pendingVerificationEmail");
              navigate("/");
            }, 2000);
          }
        } else {
          // User not logged in, try to check via profile lookup
          // This is a fallback that might not work without admin privileges
          const { data: profiles, error: profileError } = await supabase
            .from("profiles")
            .select("email_verified")
            .eq("email", email)
            .single();

          if (!profileError && profiles?.email_verified === true) {
            setIsVerified(true);
            setSuccess("🎉 Email verified! Redirecting to login...");
            setTimeout(() => {
              localStorage.removeItem("pendingVerificationEmail");
              navigate("/login", {
                state: {
                  email: email,
                  message:
                    "Email verified! Please log in to access your account.",
                },
              });
            }, 2000);
          }
        }
      } catch (error) {
        console.error("Error checking verification status:", error);
      } finally {
        setCheckingVerification(false);
      }
    };

    // Check immediately, then every 5 seconds
    checkVerificationStatus();
    const interval = setInterval(checkVerificationStatus, 5000);

    return () => clearInterval(interval);
  }, [email, isVerified, checkingVerification, navigate]);

  // Countdown timer for resend button
  useEffect(() => {
    let timer;
    if (countdown > 0 && !showResendButton) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setShowResendButton(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, showResendButton]);

  // GSAP animations
  useEffect(() => {
    if (containerRef.current && leftPanelRef.current && rightPanelRef.current) {
      const tl = gsap.timeline();

      // Set initial states
      gsap.set([leftPanelRef.current, rightPanelRef.current], {
        opacity: 0,
        x: -50,
      });

      gsap.set(rightPanelRef.current, {
        x: 50,
      });

      // Animate in
      tl.to([leftPanelRef.current, rightPanelRef.current], {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.2,
      });
    }
  }, []);

  const sendVerificationEmail = async (autoSend = false) => {
    if (isVerified) {
      if (!autoSend) {
        setSuccess("Email already verified! You can now log in.");
      }
      return;
    }

    // Double-check verification status before sending email
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user && session.user.email === email) {
        const { data, error } = await supabase.rpc("is_email_verified", {
          user_id: session.user.id,
        });

        if (!error && data === true) {
          console.log("✅ User verified during send attempt, skipping email");
          setIsVerified(true);
          setSuccess("🎉 Email already verified! You can now log in.");
          return;
        }
      }
    } catch (checkError) {
      console.error("Error checking verification before send:", checkError);
      // Continue with sending email if check fails
    }

    if (!autoSend) {
      setLoading(true);
    }
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/send-verification-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            username: email.split("@")[0],
            action: "verification",
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (!autoSend) {
          setSuccess("Verification email sent! Please check your inbox.");
          setShowResendButton(false);
          setCountdown(60);
        }
      } else {
        throw new Error(data.error || "Failed to send verification email");
      }
    } catch (err) {
      console.error("Error sending verification email:", err);
      setError(err.message || "Failed to send verification email");
    } finally {
      if (!autoSend) {
        setLoading(false);
      }
    }
  };

  const handleGoToLogin = () => {
    localStorage.removeItem("pendingVerificationEmail");
    navigate("/login");
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden"
    >
      {/* Grid background with center glow effect */}
      <div className="absolute inset-0">
        {/* Base grid pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Radial gradient mask to create center glow and edge fade effect */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, 
                rgba(184, 70, 14, 0.15) 0%, 
                rgba(184, 70, 14, 0.08) 40%, 
                rgba(184, 70, 14, 0.03) 70%,
                rgba(184, 70, 14, 0) 100%
              )
            `,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
            maskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
            WebkitMaskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
          }}
        />

        {/* Interactive grid overlay for hover effects */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: gridDimensions.rows }).map((_, row) =>
            Array.from({ length: gridDimensions.cols }).map((_, col) => (
              <div
                key={`${row}-${col}`}
                className="absolute w-10 h-10 pointer-events-auto transition-all duration-300"
                style={{
                  left: `${col * 40}px`,
                  top: `${row * 40}px`,
                  animation: "none",
                }}
                onMouseEnter={(e) => {
                  gsap.fromTo(
                    e.target,
                    {
                      backgroundColor: "rgba(184, 70, 14, 0)",
                      boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      scale: 1,
                    },
                    {
                      backgroundColor: "rgba(184, 70, 14, 0.3)",
                      boxShadow:
                        "0 0 25px rgba(184, 70, 14, 0.8), inset 0 0 15px rgba(184, 70, 14, 0.3)",
                      scale: 1.05,
                      duration: 0.2,
                      ease: "power2.out",
                    }
                  );

                  const adjacentSquares = document.querySelectorAll(
                    `[data-row="${row}"][data-col="${
                      col - 1
                    }"], [data-row="${row}"][data-col="${
                      col + 1
                    }"], [data-row="${
                      row - 1
                    }"][data-col="${col}"], [data-row="${
                      row + 1
                    }"][data-col="${col}"]`
                  );
                  adjacentSquares.forEach((square, index) => {
                    gsap.fromTo(
                      square,
                      {
                        backgroundColor: "rgba(184, 70, 14, 0)",
                        boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      },
                      {
                        backgroundColor: "rgba(184, 70, 14, 0.1)",
                        boxShadow: "0 0 10px rgba(184, 70, 14, 0.4)",
                        duration: 0.15,
                        delay: index * 0.05,
                        ease: "power2.out",
                        onComplete: () => {
                          gsap.to(square, {
                            backgroundColor: "rgba(184, 70, 14, 0)",
                            boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                            duration: 0.3,
                            ease: "power2.out",
                          });
                        },
                      }
                    );
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    backgroundColor: "rgba(184, 70, 14, 0)",
                    boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                    scale: 1,
                    duration: 0.4,
                    ease: "power2.out",
                  });
                }}
                data-row={row}
                data-col={col}
              />
            ))
          )}
        </div>
      </div>

      {/* Main Centered Container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left Panel - Verification Instructions */}
            <div
              ref={leftPanelRef}
              className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8 flex items-center justify-center order-2 lg:order-1"
            >
              <div className="w-full max-w-sm">
                {/* Logo */}
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] rounded-2xl flex items-center justify-center shadow-lg">
                    <Mail className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                  </div>
                </div>

                {/* Title */}
                <div className="text-center mb-6">
                  <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                    Verify Your Email
                  </h1>
                  <p className="text-sm lg:text-base text-gray-300">
                    We've sent a verification link to your email
                  </p>
                </div>

                {/* Email Display */}
                <div className="bg-white/5 border border-white/20 rounded-xl p-4 mb-6">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-gray-400 mr-3" />
                    <span className="text-sm font-medium text-[#B8460E] break-all">
                      {email}
                    </span>
                  </div>
                </div>

                {/* Status Messages */}
                {error && (
                  <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-2.5 rounded-xl text-sm mb-4 flex items-center">
                    <XCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {success && (
                  <div className="bg-green-900/20 border border-green-500/50 text-green-300 px-4 py-2.5 rounded-xl text-sm mb-4 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                    {success}
                  </div>
                )}

                {/* Instructions */}
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-300 mb-4">
                    Check your inbox and click the verification link to activate
                    your account.
                  </p>
                  <p className="text-xs text-gray-400">
                    Don't see the email? Check your spam folder.
                  </p>
                </div>

                {/* Resend Section */}
                <div className="space-y-4">
                  {isVerified ? (
                    <div className="bg-green-900/20 border border-green-500/50 rounded-xl p-4">
                      <div className="flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-300 mr-2" />
                        <span className="text-sm text-green-300">
                          Email verified! Redirecting to login...
                        </span>
                      </div>
                    </div>
                  ) : !showResendButton ? (
                    <div className="bg-[#B8460E]/20 border border-[#B8460E]/50 rounded-xl p-4">
                      <div className="flex items-center justify-center">
                        <Clock className="w-4 h-4 text-orange-300 mr-2" />
                        <span className="text-sm text-orange-300">
                          Resend available in {countdown}s
                        </span>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => sendVerificationEmail()}
                      disabled={loading || isVerified}
                      className="w-full bg-gradient-to-r from-[#B8460E] via-[#8B3A0E] to-[#B8460E] hover:from-[#D4541A] hover:via-[#B8460E] hover:to-[#D4541A] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 lg:py-3 px-6 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center text-sm lg:text-base shadow-lg hover:shadow-xl"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Resend Verification Email
                        </>
                      )}
                    </button>
                  )}

                  {/* Back to Login */}
                  <button
                    onClick={handleGoToLogin}
                    className="w-full bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white font-medium py-2.5 lg:py-3 rounded-xl transition-all duration-200 border border-white/20 flex items-center justify-center"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </button>
                </div>
              </div>
            </div>

            {/* Right Panel - Information */}
            <div
              ref={rightPanelRef}
              className="w-full lg:w-1/2 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] p-4 sm:p-6 lg:p-8 flex items-center justify-center order-1 lg:order-2 relative"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-white text-center max-w-md">
                <div className="mb-6">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white/20 rounded-3xl mx-auto mb-4 flex items-center justify-center shadow-2xl backdrop-blur-sm">
                    <Mail className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-white mb-2">
                    Almost There!
                  </h2>
                  <p className="text-sm lg:text-base text-orange-100 leading-relaxed">
                    We've automatically sent you a verification email. Just one
                    click and you're all set!
                  </p>
                </div>

                {/* Steps */}
                <div className="space-y-3 text-left">
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <div className="w-8 h-8 bg-orange-300 rounded-full flex items-center justify-center mr-3">
                      <span className="text-[#8B3A0E] text-sm font-bold">
                        1
                      </span>
                    </div>
                    <span className="text-sm text-orange-100">
                      Check your email inbox
                    </span>
                  </div>
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <div className="w-8 h-8 bg-orange-300 rounded-full flex items-center justify-center mr-3">
                      <span className="text-[#8B3A0E] text-sm font-bold">
                        2
                      </span>
                    </div>
                    <span className="text-sm text-orange-100">
                      Click the verification link
                    </span>
                  </div>
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <div className="w-8 h-8 bg-orange-300 rounded-full flex items-center justify-center mr-3">
                      <span className="text-[#8B3A0E] text-sm font-bold">
                        3
                      </span>
                    </div>
                    <span className="text-sm text-orange-100">
                      Access your dashboard
                    </span>
                  </div>
                </div>

                {/* Features */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-orange-300">
                        10K+
                      </div>
                      <div className="text-xs text-orange-200">
                        Active Users
                      </div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-300">
                        99.9%
                      </div>
                      <div className="text-xs text-orange-200">Uptime</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationRequired;
